import { memo } from "react";
import { Point, Series } from "../../Interfaces/chartInterfaces";
import {
  Graph_Dto,
  Graph_Point_Dto,
} from "../../Interfaces/orderbookGraphInterfaces";
import TimeseriesChart from "./TimeseriesChart";
import { toJalaliDateTime, toGregorianDateTime } from "../../Utils/date";
import { InputTypes } from "src/helper";

interface props {
  dataset: Array<Graph_Dto>;
  className?: string;
  withDate?: boolean;
  YValueType?: InputTypes;
  /**
   * Opt-in: show both date and time (in Shamsi) on the x-axis ticks and the tooltip title/values,
   * instead of the default time-only ticks. Off by default so existing charts don't change -- only
   * pass this for charts whose data can span multiple calendar days (e.g. a 30-min sampled history).
   */
  showFullDateTime?: boolean;
  decimalPlaces?: number;
  /**
   * Override auto secondary-Y (triggered by series named «مرز اجرا»).
   * Pass false to force a single shared left Y axis.
   */
  hasSecondaryYAxis?: boolean;
}

const SMA_SKIP_SERIES = new Set(["مرز اجرا"]);
const MIN_SMA_SAMPLES = 2;

/**
 * Hourly covered-call / order-book payloads hold a price from fromTime→toTime
 * and chain toTime === next fromTime. That is a step function, not sampled
 * bars — SMA does not apply (e.g. GainAfterCommissionCompoundYearlyGraph).
 */
function isContiguousHoldSeries(points: Graph_Point_Dto[]): boolean {
  if (points.length < 2) return false;
  let holdCount = 0;
  let joinCount = 0;
  for (let i = 0; i < points.length; i++) {
    if (points[i].fromTime !== points[i].toTime) holdCount += 1;
    if (i > 0 && points[i - 1].toTime === points[i].fromTime) joinCount += 1;
  }
  return (
    holdCount / points.length >= 0.5 && joinCount / (points.length - 1) >= 0.5
  );
}

/** SMA only when graphData is a sampled numeric series, not a step-hold chart. */
function graphDataSupportsSma(
  dataset?: Array<Graph_Dto> | null,
  withDate?: boolean,
): boolean {
  if (!Array.isArray(dataset) || dataset.length === 0) return false;

  return dataset.some((series) => {
    if (!series || SMA_SKIP_SERIES.has(series.name)) return false;
    const points = series.graphPoints;
    if (!Array.isArray(points) || points.length < MIN_SMA_SAMPLES) return false;

    const priced = points.filter(
      (point) =>
        point != null &&
        point.price != null &&
        Number.isFinite(Number(point.price)),
    );
    if (priced.length < MIN_SMA_SAMPLES) return false;

    // Daily charts: one close per day — SMA is meaningful.
    if (withDate) return true;

    // Contiguous from/to holds (this JSON shape) are not SMA candidates.
    if (isContiguousHoldSeries(priced)) return false;

    const times = new Set<string>();
    priced.forEach((point) => {
      if (point.fromTime) times.add(point.fromTime);
      if (point.toTime) times.add(point.toTime);
    });
    return times.size >= MIN_SMA_SAMPLES;
  });
}

export const WrappedTimeSeriesChart = memo(
  ({
    dataset,
    className,
    withDate,
    YValueType,
    showFullDateTime,
    decimalPlaces,
    hasSecondaryYAxis: hasSecondaryYAxisProp,
  }: props) => {
    const Colors: Array<string> = [
      "#519DE9",
      "#7CC674",
      "#73C5C5",
      "#8481DD",
      "#F6D173",
      "#C46100",
      "#A30000",
      "#f79cc7",
      "#255716",
    ];

    const getPoints = (graphPoints: Array<Graph_Point_Dto>) => {
      let res: Array<Point> = [];

      graphPoints?.forEach((pointDto) => {
        // Each graphPoint is a horizontal segment: same price from fromTime → toTime.
        // Adjacent items that share toTime/fromTime become a vertical step.
        const startPoint: Point = {
          x: new Date(pointDto.fromTime),
          y: pointDto.price,
          comment: pointDto.comment,
          timeLabel: toGregorianDateTime(pointDto.fromTime),
        };
        const endPoint: Point = {
          x: new Date(pointDto.toTime),
          y: pointDto.price,
          comment: pointDto.comment,
          timeLabel: toGregorianDateTime(pointDto.toTime),
        };
        // Daily charts: one sample per day (the close of the interval).
        if (withDate) {
          res.push({
            x: endPoint.x,
            y: endPoint.y,
            comment: endPoint.comment,
          });
          return;
        }
        res.push(startPoint);
        if (pointDto.fromTime !== pointDto.toTime) {
          res.push(endPoint);
        }
      });
      return res;
    };

    const getFullDateTimeLabels = () => {
      const res = new Set<string>();
      dataset?.forEach((d) => {
        d.graphPoints?.forEach((point) => {
          res.add(toJalaliDateTime(point.fromTime));
          res.add(toJalaliDateTime(point.toTime));
        });
      });
      return Array.from(res).sort();
    };

    const getFullDateTimePoints = (graphPoints: Array<Graph_Point_Dto>) => {
      let res: Array<Point> = [];

      graphPoints?.forEach((pointDto) => {
        if (pointDto.fromTime !== pointDto.toTime) {
          res.push({
            x: toJalaliDateTime(pointDto.fromTime),
            y: pointDto.price,
            comment: pointDto.comment,
            timeLabel: toJalaliDateTime(pointDto.fromTime),
          });
        }
        res.push({
          x: toJalaliDateTime(pointDto.toTime),
          y: pointDto.price,
          comment: pointDto.comment,
          timeLabel: toJalaliDateTime(
            pointDto.fromTime === pointDto.toTime
              ? pointDto.fromTime
              : pointDto.toTime,
          ),
        });
      });
      return res;
    };

    const getSeries = () => {
      if (!dataset || !dataset.length) return [];

      if (showFullDateTime) {
        return dataset.map((r) => {
          const series: Series = {
            label: r.name,
            data: getFullDateTimePoints(r.graphPoints),
          };
          return series;
        });
      }

      return dataset.map((r) => {
        const series: Series = {
          label: r.name,
          data: getPoints(r.graphPoints),
        };
        return series;
      });
    };

    const hasSecondaryYAxis =
      hasSecondaryYAxisProp ??
      dataset?.some((item) => item.name === "مرز اجرا");
    const allowSma = graphDataSupportsSma(dataset, withDate);

    const getDatasets = () =>
      getSeries().map((x: Series, index) => {
        const isThresholdSeries =
          Boolean(hasSecondaryYAxis) && x.label === "مرز اجرا";
        const sourcePoints = dataset?.[index]?.graphPoints;
        // Hold chains are steps — unless this chart has SMA, which is a sampled line.
        const stepped =
          !allowSma &&
          Array.isArray(sourcePoints) &&
          isContiguousHoldSeries(sourcePoints);
        return {
          label: x.label,
          data: x.data,
          borderColor: Colors[index],
          backgroundColor: Colors[index],
          spanGaps: false,
          yAxisID: isThresholdSeries ? "y1" : "y",
          stepped,
        };
      });

    return (
      <TimeseriesChart
        unit={"date"}
        datasets={getDatasets()}
        className={className}
        labels={showFullDateTime ? getFullDateTimeLabels() : undefined}
        YValueType={YValueType}
        showFullDateTime={showFullDateTime}
        withDate={withDate}
        decimalPlaces={decimalPlaces}
        hasSecondaryYAxis={Boolean(hasSecondaryYAxis)}
        allowSma={allowSma}
      />
    );
  },
);
